import type { ReactElement } from 'react';
import { useMe } from '@/features/auth/hooks/useAuthQueries';
import { useAuthStore } from '@/stores/auth.store';

/**
 * DashboardPlaceholder — temporary landing for the slice-0 AppShell.
 *
 * Shows a personal greeting + tenant context so a freshly-signed-in
 * user sees something useful and so the responsive shell has real
 * content to render against during responsive verification.
 *
 * Slice 1 will replace this with the real dashboard module (sales,
 * inventory, KPIs).
 */
export function DashboardPlaceholder(): ReactElement {
  const user = useAuthStore((s) => s.user);
  const { isLoading, isError } = useMe();

  const firstName = user?.profile?.firstName ?? user?.email?.split('@')[0] ?? 'equipo';
  const tenantSlug = tenantFromHost();

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {tenantSlug ? `Organización ${tenantSlug}` : 'Panel general'}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Hola, {firstName}</h1>
        <p className="text-sm text-muted-foreground">
          Estás autenticado en GlobalesERP. El contenido del panel llega en la próxima iteración.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Rol"
          value={user?.role ?? '—'}
          subtitle="Permisos asignados a tu usuario"
        />
        <StatCard
          title="Permisos"
          value={user?.permissions.length ?? 0}
          subtitle="Habilitados actualmente"
        />
        <StatCard
          title="Estado de la sesión"
          value={isLoading ? 'Cargando…' : isError ? 'Sin token' : 'Activa'}
          subtitle="Renovada cada 15 min en background"
        />
      </section>

      <section className="rounded-xl border border-dashed bg-muted/30 p-6 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Slice 0 — placeholder</p>
        <p className="mt-1">
          Los módulos de ventas, inventario y reportes se montan en PR#5+. Esta vista valida el
          shell responsive, la autenticación y el cambio de organización.
        </p>
      </section>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle: string;
}): ReactElement {
  return (
    <article className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
    </article>
  );
}

function tenantFromHost(): string {
  if (typeof window === 'undefined') return '';
  const parts = window.location.hostname.split('.');
  return parts[0] && parts[0] !== 'localhost' && parts[0] !== 'www' ? parts[0] : '';
}
